import { useMemo } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { useLocale } from "../primitives/useLocale";
import {
  muscleName,
  catalogBySlug as buildCatalogBySlug,
  programMuscles,
  programToDraft,
  type CurrentMax,
  type Exercise,
  type Program as ProgramT,
  type UnitsPreference,
} from "@heraion/shared";

import { ProgramReadView } from "../ProgramEditorV3/ProgramReadView";
import type { V3Start } from "../ProgramEditorV3/useV3Navigation";
import type { UiTheme } from "../theme";
import { makePreviewStyles } from "./previewStyles";
import { useSpanWhileOpen } from "./previewSpan";
import { ProgramSimulationSection } from "./ProgramSimulationSection";

// Read-only program preview (inline-expanded under a row's "View
// program ▾" toggle — collapsed by default, never auto-expanded).
// Lifted verbatim from apps/mobile in the Follow+Publish lift; the one
// platform seam (localized exercise-name resolution) is injected as
// resolveName, same adapter the composer uses. The colpo d'occhio: the
// simulation, an aggregate muscles row, then the program as the editor v3
// reads it with nothing to write (#358, ProgramReadView).
export function ProgramPreview({
  program,
  catalog,
  resolveName,
  unit,
  theme,
  dark,
  maxMeta,
  lockedThrough,
  start,
  onPick,
}: {
  program: ProgramT;
  catalog: Exercise[] | null;
  resolveName: (slug: string, snapshot?: string) => string;
  // Display unit for prescribed absolute loads (stored kg) — the twin of
  // PlanPreview's prop. Without it this preview showed kg/"KG" right next
  // to a correctly-localized plan preview in the same Library card.
  unit: UnitsPreference;
  theme: UiTheme;
  dark: boolean;
  /** The caller's MAXes, as the host already holds them. Absent: a row in
   *  kilos takes no band from a MAX (#353), and nothing is fetched here. */
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  /** A followed program's rounds done (lockedThroughOf): marked «✓». */
  lockedThrough?: number;
  /** The day and round it opens on (the followed card's pick). */
  start?: V3Start;
  /** A day or round picked in it: the followed card's Start follows. */
  onPick?: (p: V3Start) => void;
}) {
  const { t } = useTranslation();
  const lang = useLocale();
  const styles = makePreviewStyles(theme);
  // Open, it asks its host for the full row (PreviewSpanScope).
  useSpanWhileOpen();

  const catalogBySlug = useMemo(
    () => buildCatalogBySlug(catalog ?? []),
    [catalog],
  );

  // Derivation shared with PlanPreview + the Library tag-filter
  // (@heraion/shared muscleDerivation); sort-by-localized-name stays here.
  const muscles = useMemo(
    () =>
      programMuscles(program.sessions, catalogBySlug).sort((a, b) =>
        muscleName(a, lang).localeCompare(muscleName(b, lang)),
      ),
    [program, catalogBySlug, lang],
  );

  // Seeded in the display unit, so every line reads in the unit the athlete
  // reads. Read-only surface: nothing is saved from this draft.
  const draft = useMemo(
    () => programToDraft(program, unit, lang),
    [program, unit, lang],
  );

  return (
    <View style={styles.container}>
      <ProgramReadView
        theme={theme}
        dark={dark}
        draft={draft}
        cellUnit={unit}
        resolveName={resolveName}
        maxMeta={maxMeta}
        lockedThrough={lockedThrough}
        start={start}
        onPick={onPick}
        top={
          <View style={{ gap: theme.spacing.s3 }}>
            {/* #355: the program measured before it is followed, closed by default. */}
            <ProgramSimulationSection
              program={program}
              catalog={catalog}
              resolveName={resolveName}
              unit={unit}
              theme={theme}
            />
            {muscles.length > 0 ? (
              <View style={styles.musclesRow}>
                <Text style={styles.sectionLabel}>
                  {t("mobile.preview.muscles", { defaultValue: "Muscles" })}
                </Text>
                <View style={styles.chipWrap}>
                  {muscles.map((m) => (
                    <View key={m} style={styles.muscleChip}>
                      <Text style={styles.muscleChipText}>{muscleName(m, lang)}</Text>
                    </View>
                  ))}
                </View>
              </View>
            ) : null}
          </View>
        }
      />
    </View>
  );
}
